<script setup lang="ts">
import { nextTick, onMounted, onUnmounted, ref } from 'vue';

/**
 * Подсказки приложения — один слой на всё.
 *
 * Нативный `title` не годится по трём причинам: появляется через секунду
 * с лишним (в таблице за это время успеваешь уехать мышью), выглядит
 * по-разному в каждой системе и не подчиняется теме — в тёмной остаётся
 * светлой плашкой системного вида.
 *
 * Обёртывать каждый элемент компонентом-подсказкой в гриде нельзя: ячеек
 * на экране под сотню, и на каждую пришлось бы по нескольку узлов и своей
 * реактивности. Поэтому здесь один элемент и делегирование: подсказка
 * читается из `data-tip` ближайшего предка под курсором.
 *
 * Пауза перед показом — 260 мс. Сразу после того, как подсказка уже была
 * показана, следующая появляется без паузы: человек ведёт мышь по ряду
 * кнопок и ждать на каждой не должен. Через полсекунды без подсказок
 * «быстрый режим» гаснет.
 */

const SHOW_DELAY = 260;
const FAST_WINDOW = 500;
/** Отступ от элемента и от края окна */
const GAP = 6;
const EDGE = 8;

const text = ref('');
const x = ref(0);
const y = ref(0);
const visible = ref(false);
/*
  Пока размеры не измерены, плашка не видна.

  Место под подсказку зависит от её собственной ширины, а узнать ширину
  можно только у отрисованной. Между этими двумя кадрами она уже в
  документе — и без этого признака первый кадр показывал бы её там, где
  она в итоге не останется: короткий рывок вбок на каждой подсказке.
*/
const measured = ref(false);

const tipEl = ref<HTMLElement | null>(null);

let showTimer: ReturnType<typeof setTimeout> | undefined;
let fastUntil = 0;
let current: HTMLElement | null = null;

/**
 * Где встать, чтобы не вылезти за экран.
 *
 * Раньше плашка центрировалась по элементу через `translate(-50%)` — и у
 * крайних кнопок половина уезжала за границу окна: подсказка к «Срезам»
 * обрезалась слева ровно по первому слову, а у правого края — по
 * последнему. Теперь координата считается по измеренной ширине и
 * прижимается к краю, а не выходит за него.
 */
function place(target: HTMLElement): void {
  const el = tipEl.value;
  if (!el) return;

  const rect = target.getBoundingClientRect();
  const width = el.offsetWidth;
  const height = el.offsetHeight;

  // Сверху, а если там не помещается — снизу
  let top = rect.top - GAP - height;
  if (top < EDGE) {
    top = rect.bottom + GAP;
    // У самого низа окна прижимаем к краю: обрезанная подсказка
    // бесполезнее сдвинутой
    if (top + height > window.innerHeight - EDGE) {
      top = Math.max(EDGE, window.innerHeight - EDGE - height);
    }
  }

  const centered = rect.left + rect.width / 2 - width / 2;
  const left = Math.min(Math.max(EDGE, centered), Math.max(EDGE, window.innerWidth - EDGE - width));

  x.value = Math.round(left);
  y.value = Math.round(top);
}

async function show(target: HTMLElement, tip: string): Promise<void> {
  current = target;
  text.value = tip;
  measured.value = false;
  visible.value = true;

  await nextTick();
  // Пока ждали кадр, мышь могла уйти на другой элемент
  if (current !== target) return;
  place(target);

  /*
    Второй проход — не перестраховка.

    Ширину меряем у плашки, стоящей на прежнем месте; на новом строки
    переносятся иначе, и ширина меняется на десяток пикселей. Считанного
    по старой ширине отступа от края тогда не хватает, и подсказка снова
    выходит за границу — ровно на эту разницу. Пересчёт по итоговой
    ширине сходится сразу: перенос от позиции больше не зависит.
  */
  await nextTick();
  if (current !== target) return;
  place(target);
  measured.value = true;
}

function hide(): void {
  clearTimeout(showTimer);
  if (visible.value) fastUntil = Date.now() + FAST_WINDOW;
  visible.value = false;
  measured.value = false;
  current = null;
}

function onOver(e: Event): void {
  const target = (e.target as HTMLElement | null)?.closest<HTMLElement>('[data-tip]');
  if (!target) {
    if (current) hide();
    return;
  }
  const tip = target.dataset.tip?.trim();
  if (!tip) {
    hide();
    return;
  }
  if (target === current) return;

  clearTimeout(showTimer);
  if (Date.now() < fastUntil) {
    void show(target, tip);
    return;
  }
  showTimer = setTimeout(() => void show(target, tip), SHOW_DELAY);
}

/** Прокрутка, нажатие и уход со страницы убирают подсказку сразу. */
function onScrollOrDown(): void {
  hide();
}

function onKey(e: KeyboardEvent): void {
  if (e.key === 'Escape') hide();
}

onMounted(() => {
  document.addEventListener('pointerover', onOver, true);
  document.addEventListener('pointerdown', onScrollOrDown, true);
  document.addEventListener('focusin', onOver, true);
  document.addEventListener('focusout', onScrollOrDown, true);
  document.addEventListener('keydown', onKey, true);
  window.addEventListener('scroll', onScrollOrDown, true);
  window.addEventListener('resize', onScrollOrDown);
  window.addEventListener('blur', onScrollOrDown);
});

onUnmounted(() => {
  clearTimeout(showTimer);
  document.removeEventListener('pointerover', onOver, true);
  document.removeEventListener('pointerdown', onScrollOrDown, true);
  document.removeEventListener('focusin', onOver, true);
  document.removeEventListener('focusout', onScrollOrDown, true);
  document.removeEventListener('keydown', onKey, true);
  window.removeEventListener('scroll', onScrollOrDown, true);
  window.removeEventListener('resize', onScrollOrDown);
  window.removeEventListener('blur', onScrollOrDown);
});
</script>

<template>
  <Transition name="tip">
    <!--
      Оформление.

      Была чёрная плашка `bg-foreground` с крупной тенью: в тёмной теме
      она сходила за подсказку, а в светлой выглядела как чужая деталь —
      сплошной чёрный прямоугольник поверх светлого интерфейса, темнее
      всего остального на экране.

      Теперь плашка держится того же языка, что и остальные всплывающие
      слои: тёмно-серый, а не чёрный, тонкая светлая обводка, чтобы край
      читался на любом фоне, мягкая тень вместо жирной, шрифт мельче
      основного текста. В тёмной теме — светлее фона страницы, иначе
      подсказка сливается с тем, поверх чего висит.
    -->
    <div
      v-if="visible && text"
      ref="tipEl"
      role="tooltip"
      class="tip pointer-events-none fixed z-[100] rounded-md px-2 py-1 text-[11.5px] leading-[1.35] font-medium whitespace-pre-line"
      :class="measured ? 'opacity-100' : 'opacity-0'"
      :style="{ left: `${x}px`, top: `${y}px` }"
    >
      {{ text }}
    </div>
  </Transition>
</template>

<style scoped>
.tip {
  /* Ширина: длинные подсказки переносятся, но не растягиваются во весь
     экран — читать строку в тысячу пикселей невозможно */
  max-width: min(300px, calc(100vw - 16px));

  /* Фон непрозрачный: сквозь полупрозрачный просвечивала шапка, и текст
     подсказки читался поверх чужих букв */
  background: var(--color-slate-900);
  color: var(--color-slate-50);
  border: 1px solid color-mix(in oklab, white 12%, transparent);
  box-shadow:
    0 1px 2px rgb(0 0 0 / 0.12),
    0 4px 12px rgb(0 0 0 / 0.14);
}

/* В тёмной теме подсказка должна быть светлее страницы, а не темнее:
   иначе она пропадает на фоне, поверх которого показана */
:global(.dark) .tip {
  background: var(--color-slate-700);
  color: var(--color-slate-50);
  border-color: color-mix(in oklab, white 14%, transparent);
  box-shadow:
    0 1px 2px rgb(0 0 0 / 0.4),
    0 6px 16px rgb(0 0 0 / 0.35);
}

.tip-enter-active,
.tip-leave-active {
  transition:
    opacity 90ms ease,
    transform 90ms ease;
}

.tip-enter-from,
.tip-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .tip-enter-active,
  .tip-leave-active {
    transition: none;
  }
}
</style>
